import React from "react";
import { parseIssueLogResourceToValue } from "../../../common/controls/LogPlus/Converters";
import { KindsList, IData, IAgreementListItem, IDeviceListItem, IItSystemListItem, ILicenseListItem, IPersonListItem, IServiceListItem } from "../../../common/controls/LogPlus/IModels";
import LogPlusResourcesDataEditor from "../../../common/controls/LogPlus/LogPlusResourcesDataEditor";
import { useGetIssueLogPlusResourcesApi } from "../../../common/services/useLogPlusResourcesApi";
import { WorkflowContext } from "../../ajaxWorkflowModal/WorkflowContext";

interface ILogIdNamePairJson {
	logId: number,
	name: string,
}

interface ILogPlusPersonPairJson {
	logId: number,
	login: string,
	firstName: string,
	lastName: string,
	sapId: string,
}

type ILogPlusResourceJson = {
	Person?: ILogPlusPersonPairJson[],
	ItSystem?: ILogIdNamePairJson[],
	Agreement?: ILogIdNamePairJson[],
	License?: ILogIdNamePairJson[],
	Service?: ILogIdNamePairJson[],

	Computer?: ILogIdNamePairJson[],
	Printer?: ILogIdNamePairJson[],
	NetworkDevice?: ILogIdNamePairJson[],
	Monitor?: ILogIdNamePairJson[],
	MobileDevice?: ILogIdNamePairJson[],
	DataCarrier?: ILogIdNamePairJson[],
	Accessory?: ILogIdNamePairJson[],
	PhoneNumber?: ILogIdNamePairJson[],
};


function pairToDevice(pair: ILogIdNamePairJson, hdKind: KindsList): IDeviceListItem {
	return {
		Id: pair.logId,
		Name: pair.name,
		HdKind: hdKind,

		Description: null,
		InventoryNumber: null,
		Model: null,
		SerialNumber: null,
		Status: null,
		Type: null,
	}
}

function pairToItSystem(pair: ILogIdNamePairJson): IItSystemListItem {
	return {
		Id: pair.logId,
		Name: pair.name,

		Description: null,
		EnvironmentType: null,
		Status: null,
		Type: null,
	}
}

function pairToAgreement(pair: ILogIdNamePairJson): IAgreementListItem {
	return {
		Id: pair.logId,
		Name: pair.name,
		Description: null,
		Status: null,
		Type: null,
		AgreementNumber: null,
		ValidFrom: null,
		ValidTo: null,
	}
}

function pairToLicense(pair: ILogIdNamePairJson): ILicenseListItem {
	return {
		Id: pair.logId,
		Name: pair.name,

		Description: null,
		Status: null,
		LicenseType: null,
		LicenseTypeDescription: null,
	}
}

function pairToService(pair: ILogIdNamePairJson): IServiceListItem {
	return {
		Id: pair.logId,
		Name: pair.name,

		Icon: null,
		CommercialDescription: null,
		OwnerId: null,
		OwnerName: null,
		OwnId: null,
		ServiceType: null,
		Status: null,
		Validity: null,
		ServiceProfileGroupId: null,
		ServiceProfileGroupName: null,
		ServiceProfileId: null,
		ServiceProfileName: null,
	}
}

function pairToPerson(pair: ILogPlusPersonPairJson): IPersonListItem {
	return {
		Id: pair.logId,
		Login: pair.login,

		Cellphone: null,
		Company: null,
		Email: null,
		FirstName: pair.firstName,
		JobPosition: null,
		LastName: pair.lastName,
		MpkNumber: null,
		SapId: pair.sapId,
		Status: null,
		SupervisorId: null,
		SupervisorName: null,
		UserName: null,
		WorkPhone: null,
	}
}

function getKinds(propsName: string, propsKinds: string) {

	let kinds: KindsList[] = null;
	if (propsKinds) {
		kinds = propsKinds.split(',') as KindsList[];
	}

	if (!kinds || kinds.length == 0) {
		switch (propsName) {
			case "LogPlusDevices.Value":
				kinds = ["Computer", "DataCarrier", "MobileDevice", "Monitor", "NetworkDevice", "Printer", "PhoneNumber", "Accessory"];
				break;
			case "LogPlusItSystems.Value":
				kinds = ["ItSystem"];
				break;
			case "LogPlusPersons.Value":
				kinds = ["Person"];
				break;
			case "LogPlusAgreements.Value":
				kinds = ["Agreement"];
				break;
			case "LogPlusLicenses.Value":
				kinds = ["License"];
				break;
			case "LogPlusServices.Value":
				kinds = ["Service"];
				break;
		}
	}
	return kinds;
}

interface IProps {
	issueId: number | null,
	name: string,
	value: string,
	controlId: string,
	errorText: string,
	multiple: boolean,
	kinds: string,
	searchIn: string,
	mode: '' | 'relatedUser' | 'relatedNotifier',
	showGridTabs: string,

	filterId: string,
	relatedFilterId: string,
	manageFiltersPermission: boolean,
	gridColumns: any,
	relatedGridColumns: any,
	maxRows: number;
	loadOnFocus: boolean;
}

export default function LogPlusResources(props: IProps)
{
	const [setModel, issueResourcesData, isLoading, isError] = useGetIssueLogPlusResourcesApi();

	const [resources, setResources] = React.useState<IData>();
	const [value, setValue] = React.useState<string>("{}");
	
	let kinds: KindsList[] = getKinds(props.name, props.kinds);

	React.useEffect(() => {

		if (props.value) {
			setValue(props.value);
			let parsed: ILogPlusResourceJson = JSON.parse(props.value);
			setResources({
				Person: parsed.Person?.map(pairToPerson),
				ItSystem: parsed.ItSystem?.map(pairToItSystem),
				Agreement: parsed.Agreement?.map(pairToAgreement),
				License: parsed.License?.map(pairToLicense),
				Service: parsed.Service?.map(pairToService),

				Computer: parsed.Computer?.map(x => pairToDevice(x, "Computer")),
				DataCarrier: parsed.DataCarrier?.map(x => pairToDevice(x, "DataCarrier")),
				MobileDevice: parsed.MobileDevice?.map(x => pairToDevice(x, "MobileDevice")),
				Monitor: parsed.Monitor?.map(x => pairToDevice(x, "Monitor")),
				NetworkDevice: parsed.NetworkDevice?.map(x => pairToDevice(x, "NetworkDevice")),
				Printer: parsed.Printer?.map(x => pairToDevice(x, "Printer")),
				Accessory: parsed.Accessory?.map(x => pairToDevice(x, "Accessory")),
				PhoneNumber: parsed.PhoneNumber?.map(x => pairToDevice(x, "PhoneNumber")),
			});
			return;
		}
		if (!props.issueId) {
			
			let newResources: IData = {
				Person: null,
				ItSystem: null,
				License: null,
				Agreement: null,
				Service: null,

				Computer: null,
				DataCarrier: null,
				MobileDevice: null,
				Monitor: null,
				NetworkDevice: null,
				Printer: null,
				Accessory: null,
				PhoneNumber: null,
			};

			setResources(newResources);
			setValue(dataToValue(newResources));

			return;
		}
		setModel({
			issueId: props.issueId,
		});
	}, []);

	React.useEffect(() => {
		if (issueResourcesData) {
			let newResources = parseIssueLogResourceToValue(issueResourcesData, kinds);

			setResources(newResources);
			setValue(dataToValue(newResources));
		}
	}, [issueResourcesData]);

	function parse(source: IDeviceListItem): ILogIdNamePairJson {
		return {
			logId: source.Id,
			name: source.Name,
		}
	}

	function parseToLogIdNamePair(source: IItSystemListItem | IAgreementListItem | ILicenseListItem | IServiceListItem): ILogIdNamePairJson {
		return {
			logId: source.Id,
			name: source.Name,
		}
	}

	function parsePerson(source: IPersonListItem): ILogPlusPersonPairJson {
		return {
			logId: source.Id,
			firstName: source.FirstName,
			lastName: source.LastName,
			login: source.Login,
			sapId: source.SapId,
		}
	}

	function dataToValue(request: IData): string {

		let tableResult: ILogPlusResourceJson = {
			Person: request.Person?.map(parsePerson),
			ItSystem: request.ItSystem?.map(parseToLogIdNamePair),
			Agreement: request.Agreement?.map(parseToLogIdNamePair),
			License: request.License?.map(parseToLogIdNamePair),
			Service: request.Service?.map(parseToLogIdNamePair),

			Computer: request.Computer?.map(parse),
			DataCarrier: request.DataCarrier?.map(parse),
			MobileDevice: request.MobileDevice?.map(parse) ,
			Monitor: request.Monitor?.map(parse),
			NetworkDevice: request.NetworkDevice?.map(parse),
			
			Printer: request.Printer?.map(parse),
			Accessory: request.Accessory?.map(parse),
			PhoneNumber: request.PhoneNumber?.map(parse),
		};
		
		return JSON.stringify(tableResult);
	}

	return (<>
		<input
			type="hidden"
			name={props.name}
			value={value}
			id={props.controlId}
		/>
		<WorkflowContext.Consumer>
			{({ issueWorkflowData, setIssueWorkflowData }) => (
				<LogPlusResourcesDataEditor
					error={!!props.errorText}
					helperText={props.errorText}
					disabled={isLoading}
					multiple={props.multiple}
					kinds={kinds}
					value={resources}

					onChange={(d) => {
						setResources(d);
						setValue(dataToValue(d));
						let workflowData = { ...issueWorkflowData };
						let workflowPropData: number[] = [];

						workflowData[props.name] = workflowPropData;
						setIssueWorkflowData(workflowData);
					}}
					
					searchIn={props.searchIn}
					notifier={issueWorkflowData.Notifier}
					mode={props.mode}
					showGridTabs={props.showGridTabs}
					relatedFilterId={props.relatedFilterId}
					filterId={props.filterId}
					manageFiltersPermission={props.manageFiltersPermission}
					gridColumns={props.gridColumns}
					relatedGridColumns={props.relatedGridColumns}
					loadOnFocus={props.loadOnFocus}
					maxRows={props.maxRows}
				/>
			)}
		</WorkflowContext.Consumer>
	</>)
}